import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Navigate, Link } from 'react-router-dom';
import { LogIn, Shield, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';

const LoginPage = () => {
  const { login, loginLocal, isAuthenticated, loading } = useAuth();
  const { language } = useLanguage();
  
  // Local login state
  const [loginMode, setLoginMode] = useState('google'); // 'google' or 'local'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-slate-600" />
        </div>
      </Layout>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/lina" replace />;
  }

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLocalLoading(true);
    
    const result = await loginLocal(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    setLocalLoading(false);
  };

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center bg-slate-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-slate-700" />
              </div>
              <h2 className="text-3xl font-light text-slate-800 mb-2">
                {language === 'en' ? 'Admin Access' : 'Accès Admin'}
              </h2>
              <p className="text-slate-500 mb-6">
                {language === 'en' 
                  ? 'Sign in to access the admin panel' 
                  : 'Connectez-vous pour accéder au panneau admin'
                }
              </p>
            </div>

            {/* Login Mode Tabs */}
            <div className="flex border border-slate-200 rounded-lg mb-6 p-1">
              <button
                onClick={() => { setLoginMode('google'); setError(''); }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  loginMode === 'google' 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                Google
              </button>
              <button
                onClick={() => { setLoginMode('local'); setError(''); }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                  loginMode === 'local' 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                Email / Mot de passe
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            {loginMode === 'google' ? (
              /* Google Login */
              <Button 
                onClick={login}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 rounded-xl flex items-center justify-center gap-3"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {language === 'en' ? 'Sign in with Google' : 'Se connecter avec Google'}
              </Button>
            ) : (
              /* Local Login Form */
              <form onSubmit={handleLocalLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@exemple.com"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-slate-700">
                    Mot de passe oublié ?
                  </Link>
                </div>

                <Button 
                  type="submit"
                  disabled={localLoading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 rounded-xl flex items-center justify-center gap-2"
                  data-testid="local-login-btn"
                >
                  {localLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <LogIn className="w-5 h-5" />
                  )}
                  {language === 'en' ? 'Sign in' : 'Se connecter'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm text-slate-400">
              {language === 'en' 
                ? 'Only authorized users can access this area' 
                : 'Seuls les utilisateurs autorisés peuvent accéder à cette zone'
              }
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoginPage;
